import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Copy, Share2, Check, QrCode } from 'lucide-react';
import BusinessQRModal from './BusinessQRModal';

/**
 * BusinessShareControl — Iter 196
 *
 * Prominent "Copy Link" + "Share" card mounted in the Business Dashboard so
 * owners can grab their public URL without having to navigate to their public
 * page first. Reuses the same clipboard + Web Share API logic that powers
 * ShareInlineButton (Iter 172) — only the layout / prominence differ.
 *
 * Props:
 *   businessId   — the business UUID; resolved to the full public URL via
 *                  `window.location.origin` so it works on Preview AND
 *                  Production without any env-var changes.
 *   businessName — used as the title in the Web Share API payload.
 */
const BusinessShareControl = ({ businessId, businessName, businessLogo, businessCity }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  if (!businessId) return null;

  const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/share/business/${businessId}`;

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const doShare = async () => {
    const payload = {
      title: businessName || 'Find-2-Day',
      text: t('share.businessShareText', '{{name}} on Find-2-Day', { name: businessName || '' }),
      url,
    };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        // fall through to clipboard fallback
      }
    }
    await doCopy();
  };

  return (
    <div
      className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 p-4 shadow-sm"
      data-testid="business-share-control"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center">
          <Link2 className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-purple-900">
            {t('share.yourBusinessLink', 'Your business link')}
          </p>
          <p
            className="mt-1 text-xs text-purple-800/80 truncate font-mono"
            data-testid="business-share-url"
            title={url}
          >
            {url}
          </p>
          <p className="text-xs text-purple-800/70 mt-1.5 leading-snug">
            {t('share.copyHint', 'Share this on WhatsApp, flyers, your menu, receipts or socials so customers go straight to your page.')}
          </p>
          <div className="mt-3 flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={doCopy}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                copied
                  ? 'bg-emerald-600 text-white'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
              data-testid="share-copy-link-btn"
              aria-label={t('share.copyLink', 'Copy link')}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? t('share.linkCopied', 'Business link copied to clipboard') : t('share.copyLink', 'Copy link')}
            </button>
            <button
              type="button"
              onClick={doShare}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-purple-300 text-purple-700 hover:bg-purple-100 transition-colors"
              data-testid="share-share-btn"
              aria-label={t('share.share', 'Share')}
            >
              <Share2 className="h-4 w-4" />
              {t('share.share', 'Share')}
            </button>
            {/* Iter 197 — QR code button opens an in-app modal with preview,
                Download PNG and Print branded poster actions. */}
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-purple-300 text-purple-700 hover:bg-purple-100 transition-colors"
              data-testid="share-qr-btn"
              aria-label={t('qr.button', 'QR code')}
            >
              <QrCode className="h-4 w-4" />
              {t('qr.button', 'QR code')}
            </button>
          </div>
        </div>
      </div>
      <BusinessQRModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        businessId={businessId}
        businessName={businessName}
        businessLogo={businessLogo}
        businessCity={businessCity}
      />
    </div>
  );
};

export default BusinessShareControl;
